import type { ApiError } from '@arpm/shared';

import { useAuthStore } from '@/stores/auth.js';

/**
 * Простой fetch-клиент с автоматическим Bearer-токеном из auth store.
 *
 * Когда стабилизируется бэкенд — будет заменено на сгенерированный
 * orval-клиент. Сейчас держим минимально и руками.
 */

const baseUrl = import.meta.env.VITE_API_URL.replace(/\/$/, '');

export class ApiClientError extends Error {
  constructor(
    message: string,
    public readonly statusCode: number,
    public readonly apiError: ApiError | null,
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

/**
 * Соседние сервисы, куда кабинет ходит НАПРЯМУЮ.
 *
 * Страница участия собирается из живых данных `events` и `accreditation`, а
 * не из копии в базе ЛК: копия начала бы расходиться с оригиналом, и «во
 * сколько мой конкурсный день» отвечалось бы вчерашним расписанием. Токен
 * тот же — человек один и тот же, просто спрашивает разные сервисы о себе.
 */
export const serviceUrls = {
  events: (import.meta.env.VITE_EVENTS_API_URL ?? '').replace(/\/$/, ''),
  accreditation: (import.meta.env.VITE_ACCREDITATION_API_URL ?? '').replace(/\/$/, ''),
};

export async function apiRequest<T>(
  path: string,
  init: RequestInit = {},
  base: string = baseUrl,
): Promise<T> {
  const authStore = useAuthStore();

  const headers = new Headers(init.headers);
  if (!headers.has('Content-Type') && init.body) {
    headers.set('Content-Type', 'application/json');
  }
  if (authStore.accessToken) {
    headers.set('Authorization', `Bearer ${authStore.accessToken}`);
  }

  const response = await fetch(`${base}${path}`, {
    ...init,
    headers,
    credentials: 'omit',
  });

  if (!response.ok) {
    let apiError: ApiError | null = null;
    try {
      apiError = (await response.json()) as ApiError;
    } catch {
      // body не json
    }
    throw new ApiClientError(
      apiError?.message ?? `${response.status} ${response.statusText}`,
      response.status,
      apiError,
    );
  }

  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}

export const apiClient = {
  get: <T>(path: string) => apiRequest<T>(path, { method: 'GET' }),
  post: <T>(path: string, body?: unknown) =>
    apiRequest<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),
  put: <T>(path: string, body?: unknown) =>
    apiRequest<T>(path, { method: 'PUT', body: body ? JSON.stringify(body) : undefined }),
  patch: <T>(path: string, body?: unknown) =>
    apiRequest<T>(path, { method: 'PATCH', body: body ? JSON.stringify(body) : undefined }),
  delete: <T>(path: string) => apiRequest<T>(path, { method: 'DELETE' }),
};

/**
 * Запрос к соседнему сервису.
 *
 * Отдельная функция, а не флаг: обращение в чужой сервис должно быть видно в
 * месте вызова. Если адрес не настроен, честно говорим об этом, а не
 * стучимся в собственный API и получаем невнятную 404.
 */
export function serviceRequest<T>(
  service: keyof typeof serviceUrls,
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const base = serviceUrls[service];
  if (!base) {
    return Promise.reject(
      new ApiClientError(`Сервис «${service}» не настроен в этом окружении`, 0, null),
    );
  }
  return apiRequest<T>(path, init, base);
}
